import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Check, Play, Utensils, Building2, Mountain, Mic, Sparkles, FileText, PlayCircle, Link as LinkIcon, StickyNote } from "lucide-react";
import LanguageHUD from "@/components/LanguageHUD";
import { CharacterById } from "@/components/LanguageCharacters";
import { useT } from "@/lib/i18n";

const WORLD_META = {
  ciudad: { icon: Building2, color: "#3B82F6", titleKey: "lang.map.world.ciudad" },
  restaurante: { icon: Utensils, color: "#DC2626", titleKey: "lang.map.world.restaurante" },
  viaje: { icon: Mountain, color: "#1B2E7A", titleKey: "lang.map.world.viaje" },
};

export default function LanguageMap() {
  const t = useT();
  const { lang } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const character = localStorage.getItem("lang_guide") || "owl";

  useEffect(() => {
    (async () => {
      try {
        const [r, m] = await Promise.all([
          api.get(`/languages/${lang}/map`),
          api.get(`/languages/${lang}/materials`).catch(() => ({ data: { materials: [] } })),
        ]);
        setData(r.data);
        setMaterials(m.data.materials || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo cargar el mapa");
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  if (loading) return <div className="text-[#374151]">{t("common.loading")}</div>;
  if (!data) return null;

  const langLabel = { en: t("onb.opt.lang.en").replace("🇺🇸 ", ""), fr: t("onb.opt.lang.fr").replace("🇫🇷 ", ""), es: t("onb.opt.lang.es").replace("🇪🇸 ", "") }[lang] || lang;

  return (
    <div className="space-y-8" data-testid="language-map">
      <button
        onClick={() => navigate("/idiomas")}
        className="text-sm text-[#6B7280] hover:text-[#1B2E7A] inline-flex items-center gap-1"
        data-testid="back-to-langs"
      >
        <ArrowLeft className="h-4 w-4" /> {t("lang.map.back")}
      </button>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280]">{langLabel}</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#1A1A1A]">{t("lang.map.title")}</h1>
          <p className="text-[#6B7280] max-w-xl mt-2">{t("lang.map.subtitle")}</p>
        </div>
        <div className="shrink-0">
          <CharacterById id={character} size={100} />
        </div>
      </div>

      <LanguageHUD profile={data.profile} />

      {/* Conversación con voz CTA */}
      <button
        onClick={() => navigate(`/idiomas/conversar/${lang}`)}
        className="w-full text-left bg-gradient-to-br from-[#1B2E7A] via-[#1B2E7A] to-[#13205A] text-white rounded-2xl p-5 md:p-6 hover:shadow-lg transition-all group"
        data-testid="cta-conversar"
      >
        <div className="flex items-center gap-4 md:gap-6">
          <div className="h-14 w-14 rounded-2xl bg-[#F4B81F] text-[#1A1A1A] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
            <Mic className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#F4B81F] mb-1 inline-flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> {t("lang.map.cta.eyebrow")}
            </p>
            <h2 className="font-heading text-xl md:text-2xl">{t("lang.map.cta.title", { lang: langLabel })}</h2>
            <p className="text-xs md:text-sm text-[#E5E7EB] mt-1">{t("lang.map.cta.subtitle")}</p>
          </div>
          <ArrowLeft className="h-5 w-5 text-white rotate-180 hidden md:block" />
        </div>
      </button>

      <div className="space-y-10">
        {data.worlds.map((world, wIdx) => {
          const meta = WORLD_META[world.world] || { icon: Building2, color: "#6B7280", titleKey: null };
          const WorldIcon = meta.icon;
          const completedAll = world.lessons.every((l) => l.completed);
          const worldTitle = meta.titleKey ? t(meta.titleKey) : world.title;
          return (
            <section key={world.world} data-testid={`world-${world.world}`}>
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="h-11 w-11 rounded-xl flex items-center justify-center"
                  style={{ background: `${meta.color}15`, color: meta.color }}
                >
                  <WorldIcon className="h-5 w-5" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
                    {t("lang.map.world_label", { n: wIdx + 1 })}
                  </p>
                  <h2 className="font-heading text-2xl text-[#1A1A1A]">{worldTitle}</h2>
                </div>
                {completedAll && (
                  <span className="ml-auto text-[10px] uppercase tracking-[0.18em] bg-[#1B2E7A]/10 text-[#1B2E7A] px-2 py-1 rounded">
                    {t("lang.map.world_complete")}
                  </span>
                )}
              </div>

              {/* Path: staggered buttons */}
              <div className="relative pl-6 md:pl-0">
                <div className="absolute md:hidden left-3 top-4 bottom-4 w-0.5 bg-[#E5E7EB]" />
                <div className="grid md:grid-cols-3 gap-4">
                  {world.lessons.map((lesson, idx) => {
                    const disabled = !lesson.unlocked;
                    const stagger = `md:mt-${idx % 2 === 0 ? 0 : 10}`; // visual stagger
                    return (
                      <button
                        key={lesson.id}
                        disabled={disabled}
                        onClick={() => navigate(`/idiomas/leccion/${lesson.id}`)}
                        className={`relative text-left rounded-2xl border p-5 transition-all duration-200 ${stagger} ${
                          disabled
                            ? "bg-[#FFFFFF] border-[#E5E7EB] cursor-not-allowed opacity-70"
                            : lesson.completed
                            ? "bg-white border-[#1B2E7A]/40 hover:shadow-md"
                            : "bg-white border-[#E5E7EB] hover:-translate-y-0.5 hover:shadow-md hover:border-[#D1D5DB]"
                        }`}
                        data-testid={`lesson-${lesson.id}`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              lesson.completed
                                ? "bg-[#1B2E7A] text-white"
                                : disabled
                                ? "bg-[#F3F4F6] text-[#9CA3AF]"
                                : "bg-[#F4B81F]/20 text-[#B45309]"
                            }`}
                          >
                            {lesson.completed ? (
                              <Check className="h-5 w-5" strokeWidth={2.5} />
                            ) : disabled ? (
                              <Lock className="h-4 w-4" strokeWidth={2} />
                            ) : (
                              <Play className="h-4 w-4" strokeWidth={2.5} />
                            )}
                          </div>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                            {t("lang.map.lesson_label", { n: lesson.order })}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg text-[#1A1A1A] leading-tight">
                          {lesson.title}
                        </h3>
                        {lesson.subtitle && (
                          <p className="text-xs text-[#6B7280] mt-0.5">{lesson.subtitle}</p>
                        )}
                        <p className="text-xs text-[#6B7280] mt-2 leading-snug line-clamp-2">
                          {lesson.description}
                        </p>
                        <div className="flex items-center gap-3 mt-3 text-xs text-[#6B7280]">
                          <span className="inline-flex items-center gap-1">⚡ {lesson.xp_reward} XP</span>
                          <span className="inline-flex items-center gap-1">🪙 {lesson.coins_reward}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Material adicional cargado por admin */}
      {materials.length > 0 && (
        <section data-testid="section-materials">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#F4B81F]/15 text-[#B45309]">
              <Sparkles className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{t("lang.map.materials.eyebrow")}</p>
              <h2 className="font-heading text-2xl text-[#1A1A1A]">{t("lang.map.materials.title")}</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {materials.map((m) => {
              const Icon =
                m.resource_type === "video" ? PlayCircle :
                m.resource_type === "pdf" ? FileText :
                m.resource_type === "note" ? StickyNote : LinkIcon;
              return (
                <button
                  key={m.id}
                  onClick={() => navigate(`/idiomas/material/${m.id}`)}
                  className="text-left bg-white rounded-2xl border border-[#E5E7EB] p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
                  data-testid={`mat-card-${m.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#F3F4F6] text-[#1B2E7A] flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{m.resource_type}</p>
                      <h3 className="font-heading text-base text-[#1A1A1A] truncate">{m.title}</h3>
                      {m.description && (
                        <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{m.description}</p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        {m.completed ? (
                          <span className="text-[10px] uppercase tracking-[0.16em] text-[#1B2E7A] inline-flex items-center gap-1">
                            <Check className="h-3 w-3" /> {t("lang.map.material.completed")}
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-[0.16em] text-[#B45309] inline-flex items-center gap-1">
                            <Sparkles className="h-3 w-3" /> {t("lang.map.material.ai_exam")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
